import {assertNever} from './assert-never';
import {
  PAGE_NUMBER_ALIGNS,
  PAGE_NUMBER_FORMATS,
  PAGE_NUMBER_PLACEMENTS,
  type PageNumberAlign,
  type PageNumberConfig,
  type PageNumberFormat,
  type PageNumberPlacement,
  type PhotoRequirement,
  type RequirementOf,
} from './types';

/** A page-number element with every optional field filled in. This is the currency
 *  every helper below deals in: coercion happens ONCE, at the read boundary
 *  (`resolvePageNumberConfig`), so nothing downstream has to re-assert the
 *  invariant or decide what a missing field means. */
export type ResolvedPageNumber = Required<PageNumberConfig>;

export const DEFAULT_PAGE_NUMBER_CONFIG: ResolvedPageNumber = {
  format: 'page_n_of_m',
  placement: 'bottom',
  align: 'center',
  show_from: 1,
  start_at: 1,
};

/** Element configs are persisted inside an opaque JSON blob (the API stores
 *  `requirements` as `list[dict[str, Any]]`), so a template can hold anything
 *  here — a NaN, a string, a format this build doesn't know. Every read is
 *  coerced back into range rather than trusted, so bad data degrades to the
 *  default instead of printing "Page NaN of undefined" on a customer's report. */
function positiveInt(value: unknown, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value) && value >= 1
    ? Math.floor(value)
    : fallback;
}

function oneOf<T extends string>(options: readonly T[], value: unknown, fallback: T): T {
  return options.includes(value as T) ? (value as T) : fallback;
}

export function resolvePageNumberConfig(cfg: PageNumberConfig | undefined): ResolvedPageNumber {
  return {
    format: oneOf<PageNumberFormat>(
      PAGE_NUMBER_FORMATS,
      cfg?.format,
      DEFAULT_PAGE_NUMBER_CONFIG.format,
    ),
    placement: oneOf<PageNumberPlacement>(
      PAGE_NUMBER_PLACEMENTS,
      cfg?.placement,
      DEFAULT_PAGE_NUMBER_CONFIG.placement,
    ),
    align: oneOf<PageNumberAlign>(PAGE_NUMBER_ALIGNS, cfg?.align, DEFAULT_PAGE_NUMBER_CONFIG.align),
    show_from: positiveInt(cfg?.show_from, DEFAULT_PAGE_NUMBER_CONFIG.show_from),
    start_at: positiveInt(cfg?.start_at, DEFAULT_PAGE_NUMBER_CONFIG.start_at),
  };
}

/**
 * The text a page-number element shows on `pageIdx` (0-based), or `null` when it
 * prints nothing there — the page sits before `show_from`, so the element is not
 * rendered on that page at all.
 *
 * `start_at` re-bases the count: with `show_from: 2, start_at: 1` a five-page
 * report prints nothing, 1, 2, 3, 4. The "of M" total is therefore the LAST
 * number in the document (`start_at + totalPages - show_from`), not the physical
 * page count — "Page 1 of 4" over four numbered pages, which is what a reader
 * checking for missing pages counts.
 */
export function pageNumberLabel(
  pn: ResolvedPageNumber,
  pageIdx: number,
  totalPages: number,
): string | null {
  const pageNo = pageIdx + 1;
  if (pageNo < pn.show_from) return null;

  const n = pn.start_at + (pageNo - pn.show_from);
  const last = pn.start_at + (totalPages - pn.show_from);
  switch (pn.format) {
    case 'plain':
      return String(n);
    case 'page_n':
      return `Page ${n}`;
    case 'page_n_of_m':
      return `Page ${n} of ${last}`;
    default:
      return assertNever(pn.format, 'page number format');
  }
}

/** A page-number element with its config already resolved. The pagination reads
 *  stamps in this shape (see `pageNumberStamps` in usePreviewLayout), so nothing
 *  downstream carries a raw config around. */
export interface PageNumberStamp {
  item: RequirementOf<'page_number'>;
  pn: ResolvedPageNumber;
}

/** A stamp as it draws on ONE page. */
export interface StampOnPage extends PageNumberStamp {
  /** The running number for this page, or `null` when the page is out of the
   *  element's range — only the editor's placeholder is drawn then. */
  text: string | null;
  /** Whether this is the stamp's first in-range page. That instance owns the
   *  element's ref registration, so a stamp repeated across ten pages still has
   *  exactly one measurable instance — the rule a multi-page header group follows. */
  isOrigin: boolean;
}

/**
 * Which stamps draw on page `pageIdx`, and what each shows there.
 *
 * A stamp renders wherever its range covers; the page it was dropped on is
 * irrelevant. This is the one place that decides whether a stamp draws at all: an
 * out-of-range page shows nothing, EXCEPT that with `includePlaceholder` (the
 * editor) the origin page still yields an entry with `text: null`, so a stamp whose
 * range starts past the end of the document stays findable rather than being an
 * element that renders nowhere. The origin is clamped to the last page for the same
 * reason.
 */
export function stampsOnPage(
  stamps: readonly PageNumberStamp[],
  pageIdx: number,
  totalPages: number,
  {includePlaceholder}: {includePlaceholder: boolean},
): StampOnPage[] {
  return stamps.flatMap(({item, pn}) => {
    const text = pageNumberLabel(pn, pageIdx, totalPages);
    const isOrigin = pageIdx === Math.min(pn.show_from - 1, totalPages - 1);
    const showPlaceholder = includePlaceholder && isOrigin;
    if (text === null && !showPlaceholder) return [];
    return [{item, pn, text, isOrigin}];
  });
}

/**
 * Plain-English summary of what the current show-from / start-at pair does, shown
 * beside the two inputs. Deliberately format-agnostic ("numbered 1", not "Page 1
 * of 5") — the total page count isn't known while designing.
 */
export function describePageNumbering(pn: ResolvedPageNumber): string {
  const skipped = pn.show_from - 1;
  const numbered = `Page ${pn.show_from} is numbered ${pn.start_at}.`;
  if (skipped <= 0) return numbered;
  const pages = skipped === 1 ? 'Page 1 prints' : `Pages 1–${skipped} print`;
  return `${pages} no number. ${numbered}`;
}

/** Narrowing guard for the repeated-stamp render path. The canonical answer to "is
 *  this element placed by its own band rule rather than by the flow or by
 *  coordinates?" — use it wherever that question is asked, so the rule lives in one
 *  place. */
export function isPageNumber(item: PhotoRequirement): item is RequirementOf<'page_number'> {
  return item.type === 'page_number';
}

/** `justify-content` for the band, per the chosen alignment. */
export function pageNumberJustify(align: PageNumberAlign): 'flex-start' | 'center' | 'flex-end' {
  switch (align) {
    case 'left':
      return 'flex-start';
    case 'center':
      return 'center';
    case 'right':
      return 'flex-end';
    default:
      return assertNever(align, 'page number alignment');
  }
}

/** The band's box, as CSS insets. Exactly one of `top` / `bottom` is set — which
 *  one is the whole of `placement`, so the type says so rather than leaving both
 *  optional. Not exported: callers spread it into a style object and never
 *  discriminate on it. */
type PageNumberBand = {left: number; right: number; height: number} & (
  | {top: number; bottom?: never}
  | {bottom: number; top?: never}
);

/**
 * Geometry of the band a stamp draws in: the full content width, inside the page's
 * own top or bottom margin. The height is the margin itself — floored at
 * MIN_BAND_HEIGHT so a template with no margin on that edge doesn't hide the
 * number altogether.
 */
const MIN_BAND_HEIGHT = 16;

export function pageNumberBand(
  pn: ResolvedPageNumber,
  margins: {top: number; bottom: number; left: number; right: number},
): PageNumberBand {
  const band = {left: margins.left, right: margins.right};
  return pn.placement === 'top'
    ? {...band, top: 0, height: Math.max(margins.top, MIN_BAND_HEIGHT)}
    : {...band, bottom: 0, height: Math.max(margins.bottom, MIN_BAND_HEIGHT)};
}
